'use client';

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type UIEvent,
} from 'react';

import type { iconsData } from './icons-data';

type IconData = (typeof iconsData)[number];

export type VirtualizedIconRow =
  | {
      type: 'category';
      categoryIndex: number;
    }
  | {
      type: 'row';
      categoryIndex: number;
      rowIndex?: number;
      icons?: IconData[];
    };

export type VirtualizedIconListHandle = {
  scrollToOffset: (offset: number) => void;
  scrollToIndex: (
    index: number,
    options?: {
      align?: 'start' | 'center' | 'end' | 'auto';
      behavior?: ScrollBehavior;
    },
  ) => void;
  measure: () => void;
  resetScrollTop: () => void;
};

type VirtualizedIconListProps = {
  virtualItems: VirtualizedIconRow[];
  categorizedIcons: Array<{ key: string; label: string; icons: IconData[] }>;
  filteredIcons: IconData[];
  renderIcon: (icon: IconData) => ReactNode;
  emptyStateLabel?: string;
  isVisible: boolean;
  onScrollElementChange: (node: HTMLDivElement | null) => void;
};

const CATEGORY_ROW_HEIGHT = 22;
const ICON_ROW_HEIGHT = 40;
const ROW_GAP = 10;
const OVERSCAN_PX = 120;

function estimateRowHeight(row: VirtualizedIconRow | undefined) {
  return row?.type === 'category' ? CATEGORY_ROW_HEIGHT : ICON_ROW_HEIGHT;
}

export const VirtualizedIconList = forwardRef<
  VirtualizedIconListHandle,
  VirtualizedIconListProps
>(function VirtualizedIconList(
  {
    virtualItems,
    categorizedIcons,
    filteredIcons,
    renderIcon,
    emptyStateLabel,
    isVisible,
    onScrollElementChange,
  },
  ref,
) {
  const parentRef = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(240);

  const layout = useMemo(() => {
    const rows = virtualItems.reduce<
      Array<{
        index: number;
        item: VirtualizedIconRow;
        start: number;
        height: number;
      }>
    >((acc, item, index) => {
      const height = estimateRowHeight(item);
      const previous = acc[acc.length - 1];
      const start = previous ? previous.start + previous.height + ROW_GAP : 0;
      acc.push({ index, item, start, height });
      return acc;
    }, []);

    const totalHeight =
      rows.length > 0
        ? rows[rows.length - 1]!.start + rows[rows.length - 1]!.height
        : 0;

    return { rows, totalHeight };
  }, [virtualItems]);

  const visibleRows = useMemo(() => {
    const viewportStart = Math.max(0, scrollTop - OVERSCAN_PX);
    const viewportEnd = scrollTop + viewportHeight + OVERSCAN_PX;

    return layout.rows.filter(({ start, height }) => {
      const end = start + height;
      return end >= viewportStart && start <= viewportEnd;
    });
  }, [layout.rows, scrollTop, viewportHeight]);

  const measureViewport = useCallback(() => {
    if (!parentRef.current) return;
    setViewportHeight(parentRef.current.clientHeight);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      scrollToOffset: (offset) => {
        if (!parentRef.current) return;
        parentRef.current.scrollTop = offset;
        setScrollTop(offset);
      },
      scrollToIndex: (index, options) => {
        const row = layout.rows[index];
        if (!row || !parentRef.current) return;

        let nextOffset = row.start;
        if (options?.align === 'center') {
          nextOffset = Math.max(
            0,
            row.start - (viewportHeight - row.height) / 2,
          );
        } else if (options?.align === 'end') {
          nextOffset = Math.max(0, row.start - (viewportHeight - row.height));
        }

        parentRef.current.scrollTo({
          top: nextOffset,
          behavior: options?.behavior ?? 'auto',
        });
        setScrollTop(nextOffset);
      },
      measure: measureViewport,
      resetScrollTop: () => {
        if (!parentRef.current) return;
        parentRef.current.scrollTop = 0;
        setScrollTop(0);
      },
    }),
    [layout.rows, measureViewport, viewportHeight],
  );

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    measureViewport();

    const resizeObserver = new ResizeObserver(() => {
      measureViewport();
    });

    if (parentRef.current) {
      resizeObserver.observe(parentRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [isVisible, measureViewport]);

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    setScrollTop(event.currentTarget.scrollTop);
  };

  return (
    <div
      ref={(node) => {
        parentRef.current = node;
        onScrollElementChange(node);
      }}
      onScroll={handleScroll}
      className="max-h-60 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
    >
      {filteredIcons.length === 0 ? (
        <div className="text-center text-muted-foreground">
          {emptyStateLabel ?? 'No icon found'}
        </div>
      ) : (
        <div
          className="relative w-full overscroll-contain"
          style={{
            height: `${layout.totalHeight}px`,
          }}
        >
          {visibleRows.map(({ index, item, start, height }) => {
            const itemStyle = {
              position: 'absolute' as const,
              top: 0,
              left: 0,
              width: '100%',
              height: `${height}px`,
              transform: `translateY(${start}px)`,
            };

            if (item.type === 'category') {
              const category = categorizedIcons[item.categoryIndex];
              return (
                <div
                  key={`category-${index}`}
                  style={itemStyle}
                  className="py-1"
                >
                  <h3 className="text-sm font-medium capitalize text-foreground">
                    {category?.label ?? ''}
                  </h3>
                </div>
              );
            }

            return (
              <div key={`row-${index}`} data-index={index} style={itemStyle}>
                <div className="grid grid-cols-5 gap-2 w-full">
                  {item.icons!.map(renderIcon)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});
